/**
 * Host-ready gate primitives — shared edit-mode / busy-mode probe for
 * every host-write path that goes through the FactSet intent bridge.
 *
 * **Why this lives here.** Two unrelated consumers need exactly the
 * same probe-and-wait gate today:
 *
 *   - `excelService.ts::applyWorkbookModificationsViaIntents` —
 *     bracket of `WorkbookModification` writes (cells, ranges, charts,
 *     tables, etc.).
 *   - `services/openTemplate.ts::openTemplateInExcel` —
 *     `SetCalculationMode("manual")` + `InsertWorksheetsFromBase64`
 *     pair that materialises a FactSet template.
 *
 * Both consumers need to:
 *   1. Probe Excel's `isInEditMode` / `isBusy` flags.
 *   2. Wait a small grace window (see `DEFAULT_HOST_READY_SCHEDULE`)
 *      for either to clear.
 *   3. Either let the write start, or abort with a reason-specific,
 *      actionable user message (`Press Enter / Escape` vs `Wait a
 *      moment`).
 *
 * Keeping these definitions in one module avoids a sideways import
 * edge from `services/openTemplate.ts` into the much larger
 * `excel/excelService.ts`, and gives future host-write paths (e.g. a
 * dedicated chart-insert service) a stable seam to reuse.
 *
 * The module is bridge-only — the probe goes through
 * `getActiveExcelContext` from `excelHostOps.ts`, which itself routes
 * to the FactSet bridge transport. Office.js mode does NOT surface
 * `isInEditMode` / `isBusy`, so callers in that mode fall back to
 * in-flight error detection (see `isEditModeError`).
 */

import {
  getActiveExcelContext as getActiveExcelContextViaBridge,
  getActiveCellAddress as getActiveCellAddressViaBridge,
  selectRange as selectRangeViaBridge,
} from "../excelHostOps";

/**
 * Office.js error code surfaced when an operation is rejected because Excel
 * is mid-cell-edit (user typing in a cell, prior write left the host in
 * edit state). The same string also leaks through the FactSet bridge when
 * the underlying host-side call wraps Office.js, so we match both via
 * `isEditModeError` below.
 */
export const EDIT_MODE_ERROR_CODE = "InvalidOperationInCellEditMode";

export type StuckHostReadyReason = "stuck_edit" | "stuck_busy";

export interface HostReadyRetryCopy {
  blocker: string;
  recovery: string;
  messageOneLiner: string;
  actionLabel: string;
  queueShortLabel: string;
}

/**
 * Reason-specific user-facing copy for host-ready blocks.
 *
 * `retried: true` is reserved for paths that already waited/retried via
 * `waitForHostReady` before surfacing the message. `retryAction: true`
 * is reserved for surfaces that actually render a Retry button.
 */
export function hostReadyRetryCopy(
  reason: StuckHostReadyReason,
  opts: { retried?: boolean; retryAction?: boolean } = {},
): HostReadyRetryCopy {
  const retried = opts.retried === true;
  const retryAction = opts.retryAction ?? retried;
  const blocker =
    reason === "stuck_edit"
      ? "Excel is editing a cell."
      : "Excel is busy calculating or showing a dialog.";
  const retryTail =
    reason === "stuck_edit"
      ? retryAction
        ? "then click Retry."
        : "then try again."
      : retryAction
        ? "then click Retry."
        : "then try again.";
  const recovery =
    reason === "stuck_edit"
      ? retried
        ? `I waited and retried automatically, but Excel still was not ready. Press Enter to finish the edit or Esc to cancel it, ${retryTail}`
        : `Press Enter to finish the edit or Esc to cancel it, ${retryTail}`
      : retried
        ? `I waited and retried automatically, but Excel still was not ready. Wait for Excel to finish, ${retryTail}`
        : `Wait for Excel to finish, ${retryTail}`;
  const actionLabel =
    reason === "stuck_edit"
      ? "Retry after editing cell"
      : "Retry when Excel is ready";
  const queueShortLabel =
    reason === "stuck_edit" ? "in cell-edit mode" : "busy";
  return {
    blocker,
    recovery,
    messageOneLiner: `${blocker} ${recovery}`,
    actionLabel,
    queueShortLabel,
  };
}

export function hostReadyRetryButtonLabel(
  reasons: ReadonlyArray<StuckHostReadyReason>,
  count: number,
): string {
  const safeCount = Math.max(1, count);
  const skipped = safeCount === 1 ? "skipped step" : `${safeCount} skipped steps`;
  const uniqueReasons = new Set(reasons);
  if (uniqueReasons.size === 1 && uniqueReasons.has("stuck_edit")) {
    return `Retry ${skipped} after editing cell`;
  }
  if (uniqueReasons.size === 1 && uniqueReasons.has("stuck_busy")) {
    return `Retry ${skipped} when Excel is ready`;
  }
  return `Retry ${skipped}`;
}

export function hostReadyPauseMessage(reason: StuckHostReadyReason): string {
  return reason === "stuck_edit"
    ? "Excel is in editing mode. Cancel editing and retry."
    : "Excel is busy. Wait until Excel is ready, then retry.";
}

export function hostReadyPausedStepText(reason: StuckHostReadyReason): string {
  return reason === "stuck_edit"
    ? "Paused - Excel is in editing mode. Exit editing mode and retry."
    : "Paused - Excel is busy. Wait until Excel is ready and retry.";
}

/**
 * Default user-facing message shown when the apply is aborted because Excel
 * is in cell-edit mode. Retryable gate paths should call
 * `hostReadyRetryCopy("stuck_edit", { retried: true })` instead.
 *
 * Used when `isInEditMode` is the (only or primary) blocker. When `isBusy`
 * is the only blocker we surface `BUSY_USER_MESSAGE` instead — the
 * recovery action is different (wait vs. press a key).
 */
export const EDIT_MODE_USER_MESSAGE =
  hostReadyRetryCopy("stuck_edit").messageOneLiner;

/**
 * User-facing message shown when the apply is aborted because Excel
 * reports `isBusy: true` (long calculation in progress, modal dialog
 * open, or other host-blocking state). The recovery action here is just
 * "wait", not "press a key" — separate message keeps the user
 * instruction accurate.
 */
export const BUSY_USER_MESSAGE =
  hostReadyRetryCopy("stuck_busy").messageOneLiner;

/**
 * Detect whether an error indicates Excel is in cell-edit mode. Checks
 * both the canonical Office.js `error.code` and a substring of
 * `error.message` so a bridge-wrapped error (which strips the typed
 * `code` but preserves the message) still resolves correctly.
 */
export function isEditModeError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const obj = error as { code?: unknown; message?: unknown };
  if (obj.code === EDIT_MODE_ERROR_CODE) return true;
  if (typeof obj.message === "string" && obj.message.includes(EDIT_MODE_ERROR_CODE)) {
    return true;
  }
  return false;
}

/**
 * Typed reason returned by `waitForHostReady`.
 *
 *   - `ready`         — host was clean on the first probe (common case)
 *   - `cleared`       — host cleared after one or more waits
 *   - `stuck_edit`    — `isInEditMode` was set on the last probe and
 *                       never cleared (with or without `isBusy`); surface
 *                       `EDIT_MODE_USER_MESSAGE`. When both flags are
 *                       set we still report `stuck_edit` because pressing
 *                       Enter / Escape is the only user-actionable step
 *                       (and busy may itself be a downstream effect of
 *                       the cell-edit lock).
 *   - `stuck_busy`    — `isBusy` only (no edit mode); surface
 *                       `BUSY_USER_MESSAGE` since the recovery is "wait".
 *   - `probe_failed`  — the bridge probe threw; we can't tell the host's
 *                       state, so the caller should let the apply proceed
 *                       and rely on the in-flight error catch as the
 *                       safety net.
 */
export type HostReadyReason =
  | "ready"
  | "cleared"
  | "stuck_edit"
  | "stuck_busy"
  | "probe_failed";

/**
 * Result of a `waitForHostReady` call. `ready` is the boolean answer
 * (proceed or not); `reason` carries the typed diagnostic the caller
 * uses to pick the user-facing message; `attempts` records how many
 * probes were made (for diagnostics / telemetry).
 */
export interface HostReadyResult {
  ready: boolean;
  reason: HostReadyReason;
  attempts: number;
}

/**
 * Default per-attempt back-off schedule for `waitForHostReady`.
 *
 * Each entry is the delay (ms) between the previous probe and the
 * next one. The first probe runs immediately; with the default
 * `[200, 400, 800, 1600]` schedule that gives probes at t=0, t=200,
 * t=600, t=1400, t=3000 - i.e. up to ~3s of total wait before the gate
 * gives up and returns `stuck_*`.
 *
 * The schedule is deliberately short. The transient edit / busy
 * states this absorbs (user pressed Enter, brief Office.js modal,
 * short calc bracket) clear within a few hundred ms in practice;
 * a user actively typing won't clear regardless of how long we
 * wait, so a long budget just stalls the agent for no win. Pair
 * with the patient escalation in `applyWorkbookModificationsViaIntents`
 * for host states that require user action or a longer calculation wait.
 */
export const DEFAULT_HOST_READY_SCHEDULE: ReadonlyArray<number> = [
  200, 400, 800, 1600,
];

/**
 * Patient back-off schedule for trailing operations that run AFTER the
 * apply burst has already landed — currently the end-of-run
 * `HarmonizedRefreshWorksheets` + `ApplicationCalculate` pair fired by
 * `finalizeRefreshAndRecalc` in `ExcelApp.tsx`.
 *
 * This short schedule is the first phase before callers may escalate to
 * `waitForHostReadyPatient`. The trade-off: a few extra seconds absorbs
 * transient edit/busy states before showing longer "exit edit mode" UI.
 *
 * Probes at t=0, t=500, t=1500, t=3500, t=6500 — i.e. up to ~6.5s of
 * total wait before the caller decides whether to proceed, fall back, or
 * enter the 5-minute patient wait.
 */
export const PATIENT_HOST_READY_SCHEDULE: ReadonlyArray<number> = [
  500, 1000, 2000, 3000,
];

/**
 * Human-reaction-time poll cadence for `waitForHostReadyPatient`.
 *
 * Used after `waitForHostReady` reports `stuck_edit` / `stuck_busy`
 * — the human now has to physically press Enter/Escape or wait for
 * a calc bracket to clear. Polling every ~1s for up to 5 minutes gives
 * the user plenty of time to react while the caller keeps the UI informed.
 * The short gate has just probed, so the patient loop waits `pollMs` before
 * each single-shot reprobe.
 *
 * 300 entries × 1000ms = 5 minutes total budget. The cancel button in chat
 * remains the escape hatch if the user wants to give up sooner.
 */
export const PATIENT_USER_RESPONSE_POLL_MS = 1000;
export const PATIENT_USER_RESPONSE_MAX_ATTEMPTS = 300;

/**
 * Pre-apply gate: probe `GetActiveExcelContext` and wait briefly for
 * `isInEditMode` / `isBusy` to clear before letting writes start.
 *
 * Why this exists: if Excel is mid-cell-edit (or busy with a long calc /
 * modal) when an apply begins, every subsequent write intent fails — most
 * notoriously with `InvalidOperationInCellEditMode` for the edit-mode
 * variant — and the entire plan cascades silent failures. We probe the
 * host on a short back-off schedule (`DEFAULT_HOST_READY_SCHEDULE`,
 * ~3s total) and then either let the apply proceed or abort with a
 * clear, actionable message — see the comment on the schedule constant
 * above for budget rationale.
 *
 * Bridge-mode only — Office.js does not surface `isInEditMode` /
 * `isBusy` and the caller falls back to in-flight error detection there.
 * The probe is read-only and deliberately calls the raw
 * `getActiveExcelContextViaBridge` (not the swallowing
 * `safeGetActiveExcelContext`) so the caller can distinguish "host
 * clean" from "probe itself failed".
 *
 * The function tracks the last observed `(isInEditMode, isBusy)` pair so
 * a `stuck_*` return tells the caller exactly which flag was the
 * blocker — needed because the user-facing recovery message differs
 * between "press Enter / Escape" (edit) and "wait a moment" (busy).
 *
 * @param schedule
 *   Per-attempt wait intervals (ms). The first probe runs
 *   immediately; subsequent probes wait `schedule[i-1]` ms first.
 *   Defaults to `DEFAULT_HOST_READY_SCHEDULE` (5 probes, ~3s
 *   total). Pass `[]` to make a single probe with no waits.
 */
export async function waitForHostReady(
  schedule: ReadonlyArray<number> = DEFAULT_HOST_READY_SCHEDULE,
): Promise<HostReadyResult> {
  // The number of probe attempts is `schedule.length + 1` — one
  // up-front, then one after each scheduled wait. Defensive: never
  // run zero attempts (would fall through to the post-loop block and
  // mis-report `stuck_busy` with `lastBusy === false`).
  const totalAttempts = Math.max(1, schedule.length + 1);
  let lastInEdit = false;
  let lastBusy = false;
  for (let attempt = 0; attempt < totalAttempts; attempt++) {
    try {
      const ctx = await getActiveExcelContextViaBridge();
      lastInEdit = ctx.isInEditMode === true;
      lastBusy = ctx.isBusy === true;
      if (!lastInEdit && !lastBusy) {
        return {
          ready: true,
          reason: attempt === 0 ? "ready" : "cleared",
          attempts: attempt + 1,
        };
      }
      console.warn(
        `[Excel] Pre-apply gate (attempt ${attempt + 1}/${totalAttempts}): ` +
          `isInEditMode=${lastInEdit} isBusy=${lastBusy}`
      );
      if (attempt < totalAttempts - 1) {
        const waitMs = schedule[attempt] ?? 0;
        if (waitMs > 0) {
          await new Promise<void>((resolve) => setTimeout(resolve, waitMs));
        }
      }
    } catch (e) {
      console.warn("[Excel] Pre-apply host-ready probe failed:", e);
      return { ready: false, reason: "probe_failed", attempts: attempt + 1 };
    }
  }
  // `lastInEdit` wins over `lastBusy` because pressing Enter / Escape is
  // the only user-actionable step — busy alone may even be a downstream
  // effect of the cell-edit lock, so the edit-mode message is what helps
  // the user recover.
  const reason: HostReadyReason = lastInEdit ? "stuck_edit" : "stuck_busy";
  return { ready: false, reason, attempts: totalAttempts };
}

/**
 * Result of `waitForHostReadyPatient`. Mirrors `HostReadyResult` but
 * adds `initialReason` — the reason the patient gate was entered
 * (i.e. why we started waiting on the user). Useful so the caller
 * can keep showing a "press Enter / Escape" status throughout the
 * extended wait even after a transient `stuck_busy` blip clears.
 */
export interface PatientHostReadyResult extends HostReadyResult {
  initialReason: HostReadyReason;
}

/**
 * Options for `waitForHostReadyPatient`. All are optional.
 *
 *   - `signal`         — AbortSignal. When aborted mid-wait the function
 *                        throws `DOMException("AbortError")` so the
 *                        chat-side cancel button stays effective.
 *   - `onProgress`     — Invoked at the start of each poll iteration
 *                        while still stuck. Caller uses this to keep
 *                        the loading status fresh ("waiting…") and
 *                        surface attempt counts.
 *   - `pollMs`         — Override the per-attempt wait (default 1000ms).
 *   - `maxAttempts`    — Override the total number of probes (default 300).
 *   - `initialResult`  — Phase-1 result the caller already ran (e.g. a
 *                        template-specific gate with extra busy
 *                        retries). When supplied, the patient function
 *                        skips its own internal `waitForHostReady` call
 *                        and goes straight to the patient poll loop if
 *                        the initial result was stuck. Avoids running
 *                        the short gate twice (each costs ~1.4s of
 *                        virtual time + 5 host probes).
 */
export interface WaitForHostReadyPatientOptions {
  signal?: AbortSignal;
  onProgress?: (info: {
    attempt: number;
    maxAttempts: number;
    reason: HostReadyReason;
    initialReason: HostReadyReason;
  }) => void;
  pollMs?: number;
  maxAttempts?: number;
  initialResult?: HostReadyResult;
}

/**
 * Patient variant of `waitForHostReady` for cases where a stuck host
 * requires a human action (commit/cancel a cell edit, dismiss a
 * dialog, etc.) rather than a fast transient.
 *
 * Behaviour:
 *
 *   1. Runs `waitForHostReady` once on the default short schedule —
 *      this absorbs sub-second transients like a brief calc spike
 *      with no UX cost.
 *   2. If that returns `ready` / `cleared` / `probe_failed`, returns
 *      immediately with the same reason (probe_failed falls through to
 *      the caller's in-flight error catch, identical to non-patient
 *      callers).
 *   3. Otherwise enters a polling loop with ~1s cadence for up to
 *      5 minutes. After each wait, every poll is a single
 *      `waitForHostReady([])` (one probe, no internal wait). On any clear
 *      probe returns `cleared`. On
 *      `probe_failed` returns immediately. After `maxAttempts` of
 *      sustained stuck state returns the last `stuck_*` reason so
 *      the caller can surface the error.
 *
 * The abort signal is honoured between polls — if the user clicks
 * cancel mid-wait, the next poll boundary throws `AbortError` and
 * the open chain unwinds normally (same path as a Ctrl+C during a
 * long FactSet download).
 */
export async function waitForHostReadyPatient(
  options: WaitForHostReadyPatientOptions = {},
): Promise<PatientHostReadyResult> {
  const {
    signal,
    onProgress,
    pollMs = PATIENT_USER_RESPONSE_POLL_MS,
    maxAttempts = PATIENT_USER_RESPONSE_MAX_ATTEMPTS,
    initialResult,
  } = options;

  const throwIfAborted = (): void => {
    if (signal?.aborted) {
      // Mirror Office.js convention: aborts surface as DOMException
      // so the outer catch in `handleQuery` can route them to the
      // unified "🛑 Run cancelled" path without bespoke detection.
      throw new DOMException("Aborted by user", "AbortError");
    }
  };

  throwIfAborted();

  // Phase 1 — short schedule. Same budget as the regular gate;
  // returns immediately if the host clears within the first ~1.4s.
  // Callers that already ran their own phase-1 (e.g. the template
  // open gate, which adds an extra busy-retry pass) pass it in via
  // `initialResult` so we don't burn another 5 probes / ~1.4s
  // re-running the same check.
  const fast = initialResult ?? (await waitForHostReady());
  if (fast.ready) {
    return { ...fast, initialReason: fast.reason };
  }
  if (fast.reason === "probe_failed") {
    return { ...fast, initialReason: fast.reason };
  }

  const initialReason: HostReadyReason = fast.reason;
  let lastReason: HostReadyReason = fast.reason;
  let lastAttempts = fast.attempts;

  // Phase 2 — patient polling. One probe per `pollMs`. Each poll uses
  // `waitForHostReady([])` so the probe itself is "single shot with no
  // internal wait" — we own the cadence here.
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    onProgress?.({
      attempt: attempt + 1,
      maxAttempts,
      reason: lastReason,
      initialReason,
    });

    // Wait first — we already probed once at the end of phase 1.
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        cleanup();
        resolve();
      }, pollMs);
      const onAbort = (): void => {
        cleanup();
        reject(new DOMException("Aborted by user", "AbortError"));
      };
      const cleanup = (): void => {
        clearTimeout(timer);
        signal?.removeEventListener("abort", onAbort);
      };
      if (signal?.aborted) {
        cleanup();
        reject(new DOMException("Aborted by user", "AbortError"));
        return;
      }
      signal?.addEventListener("abort", onAbort, { once: true });
    });

    throwIfAborted();

    const probe = await waitForHostReady([]);
    lastAttempts += probe.attempts;
    if (probe.ready) {
      return {
        ready: true,
        reason: "cleared",
        attempts: lastAttempts,
        initialReason,
      };
    }
    if (probe.reason === "probe_failed") {
      // Probe failed mid-patient-wait — treat as the same fall-through
      // as a phase-1 probe failure (caller relies on the in-flight
      // error catch). Don't keep polling against a broken bridge.
      return {
        ready: false,
        reason: "probe_failed",
        attempts: lastAttempts,
        initialReason,
      };
    }
    lastReason = probe.reason;
  }

  return {
    ready: false,
    reason: lastReason,
    attempts: lastAttempts,
    initialReason,
  };
}

/**
 * Result of `drainEditModeAfterWrite`. Tells the caller what the
 * drain actually did so logs / telemetry can distinguish "host was
 * already clean" from "we had to force a commit" from "drain itself
 * failed". Never thrown — drain is best-effort and can't be allowed
 * to break the apply / open chain.
 *
 *   - `clean`        — host reported `isInEditMode: false` on the
 *                       initial probe. No action taken. The common
 *                       happy path.
 *   - `cleared_naturally` — host was stuck initially but the flag
 *                       cleared during the short backoff polls. No
 *                       commit forced; the staleness resolved on its
 *                       own.
 *   - `forced_commit` — host was still stuck after the backoff, and
 *                       we issued `SelectRange` on the active cell
 *                       to force Excel to exit edit mode. This is
 *                       the "AI write left a stuck flag" recovery
 *                       path that motivates this helper.
 *   - `failed`       — drain itself errored (probe failed, no active
 *                       cell, selectRange rejected). The post-write
 *                       state is undefined; subsequent ops fall
 *                       through to the next host-ready gate.
 *   - `skipped_busy` — host was stuck on `isBusy: true` (no edit
 *                       mode). SelectRange can't force a busy host
 *                       to free up — it just sits in the same queue
 *                       behind whatever is busy. Leave the next
 *                       gate to wait for it.
 */
export type DrainEditModeReason =
  | "clean"
  | "cleared_naturally"
  | "forced_commit"
  | "failed"
  | "skipped_busy";

/**
 * Best-effort "drain any stuck cell-edit state" sweep, intended to
 * run AFTER an AI write burst completes (apply step, template open).
 *
 * **Why this exists.** Some bridge writes leave the host's
 * `isInEditMode` flag set even after the value has been committed
 * — a host-side flag staleness bug that we can't fix from this side
 * of the bridge. The result: the very next op (could be another AI
 * step, could be a user-issued template open) hits the pre-apply
 * host-ready gate and either stalls in the patient wait or surfaces
 * the "Excel is currently editing a cell" message — for an edit
 * that the AI itself left behind, not anything the user did.
 *
 * **What this does.**
 *
 *   1. Probe once. If the host reports `isInEditMode: false`, we
 *      return `"clean"` immediately. This is the common case after
 *      a well-behaved write.
 *   2. If stuck on edit, poll up to 3 more times at 200/400/800ms.
 *      Most flag-staleness clears within ~1s without any action on
 *      our side — issuing SelectRange in those cases would be a
 *      pointless round trip.
 *   3. If still stuck on edit after the backoff, fetch the active
 *      cell address and issue a `SelectRange` to that same address.
 *      Selecting forces Excel to exit edit mode (it can't be
 *      simultaneously editing AND selecting a different cursor
 *      target), and selecting the same cell is a visual no-op —
 *      the user's cursor doesn't move.
 *   4. If the host reports `isBusy: true` (with no edit mode),
 *      return `"skipped_busy"` — SelectRange can't free a busy
 *      host, only the next gate's wait can.
 *
 * **Safety / non-throwing.** Every branch swallows its own errors
 * and returns `"failed"`. The drain must never be allowed to break
 * the apply or open chain. The risk of clobbering live user input
 * is low: drain only fires after the AI just finished a step, and
 * the user typically isn't typing into a cell at that moment. Even
 * if they are, SelectRange on the active cell commits their text
 * the same way pressing Enter would.
 *
 * **Where to call it.** Wire into:
 *
 *   - `excelService.ts::applyWorkbookModificationsViaIntents`'s
 *     `finally` block (after `endUndo` + calc-mode restore).
 *   - `openTemplate.ts::openTemplateInExcel`'s success path
 *     (after the final `setCalc:auto` restore).
 *
 * Both call sites already have the "writes are done" semantics
 * that drain wants.
 */
export async function drainEditModeAfterWrite(): Promise<DrainEditModeReason> {
  // Stage 1 — initial probe. Fast bail when the host is already clean,
  // which is the overwhelmingly common case after a successful write.
  let ctx;
  try {
    ctx = await getActiveExcelContextViaBridge();
  } catch (e) {
    console.warn(
      "[Excel] Edit-mode drain initial probe failed; skipping drain:",
      e
    );
    return "failed";
  }
  if (ctx.isInEditMode !== true) {
    if (ctx.isBusy === true) {
      // Edge case: host is busy but not editing. Drain can't help — the
      // next gate's wait is the right tool. Surface the reason so logs
      // tell engineers "we noticed, we did nothing on purpose".
      return "skipped_busy";
    }
    return "clean";
  }

  // Stage 2 — short backoff. Flag-staleness often clears within ~1s
  // without any action on our side. Don't burn a SelectRange round
  // trip until we've given the host a beat to settle.
  const backoffMs = [200, 400, 800] as const;
  for (const ms of backoffMs) {
    await new Promise<void>((resolve) => setTimeout(resolve, ms));
    try {
      const reprobe = await getActiveExcelContextViaBridge();
      if (reprobe.isInEditMode !== true) {
        return "cleared_naturally";
      }
    } catch (e) {
      console.warn(
        "[Excel] Edit-mode drain backoff probe failed; skipping drain:",
        e
      );
      return "failed";
    }
  }

  // Stage 3 — force-commit via SelectRange on the active cell. Selecting
  // the cell currently being edited forces Excel to commit the in-flight
  // edit (it can't be simultaneously editing AND selecting), and selecting
  // the same cell is a visual no-op so the user's cursor doesn't jump.
  //
  // The address from `GetActiveCellAddress` is a bare cell like `"B3"`
  // with no sheet prefix (see `spreadsheetIntents.ts` —
  // `ActiveCellAddressResponse`), so we don't populate `worksheetName`
  // here. `referenceType: "absolute"` mirrors every other interactive
  // SelectRange call site (`excelService.ts`, `ttfCellDispatch.ts`);
  // the host treats the address as a literal cell reference.
  try {
    const address = await getActiveCellAddressViaBridge();
    if (!address) {
      // No active cell to target — drain can't proceed. Rare; happens
      // if the workbook has no active sheet (just closed, etc.). Log
      // and bail rather than risk a SelectRange with a malformed
      // address payload.
      console.warn(
        "[Excel] Edit-mode drain: no active cell address; cannot force commit."
      );
      return "failed";
    }
    await selectRangeViaBridge({ address, referenceType: "absolute" });
    console.log(
      `[Excel] Edit-mode drain: forced commit via SelectRange on ${address}.`
    );
    return "forced_commit";
  } catch (e) {
    console.warn(
      "[Excel] Edit-mode drain via SelectRange failed; leaving host as-is:",
      e
    );
    return "failed";
  }
}
